// ⚠️ LEGACY — DO NOT SPEND EFFORT HERE. Owner directive, 2026-08-20:
// "let's ignore the legacy gallery altogether we aren't using it."
//
// This is the OLD /ads gallery. The product's primary nav points at
// /product-ads (see App.tsx). The owner does not open this page.
//
// DO NOT add features here, and do not treat it as "the ads page" just
// because of the filename. Three separate changes were built here and were
// invisible to the owner as a result:
//   - the downscaled video tile URL (PR #243/#60) — gridDisplayUrl below is
//     the ONLY consumer of previewVideoUrl; /product-ads still pulls the full
//     1080p master
//   - the hardcoded 12-ad render cap fix — the real cap was in ProductAds
//   - the retailer product-page link (PR #63) — landed here only, so the
//     owner correctly reported not seeing it
//
// WHERE TO WORK INSTEAD: pages/ProductAds/index.tsx, which defines
// AdThumbnail and AdDetailModal — imported by ProductAds, UgcAds,
// CampaignDetail, Campaigns, agent/ResourceCard and GenerationInspectorModal.
// Fix those two components and six surfaces change at once.
//
// Kept only because /ads is still a routed fallback. Bug fixes that affect
// correctness are fine; new features are not.

// Ads page — gallery of rendered creatives produced by the renderer.
//
// Phase 1: lists Ad docs from GET /api/ads filtered by current
// brand. Each row is a Cloudinary thumbnail + the on-doc copy
// snapshot + template/ratio/status. Click opens a detail modal.
//
// Filter state: campaignId from query string (set by the wizard's
// post-generate redirect to /ads?campaignRunId=...). campaignRunId
// scopes to a single Generate Ads click (one batch). Status filter
// defaults to "draft" since freshly-rendered ads land in draft.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Card, CardBody, VStack, HStack, Text, Heading, Button, SimpleGrid,
  Badge, Image, Box, Spinner, useDisclosure, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalCloseButton, Select, Progress, useToast, Link,
  Input, Alert, AlertIcon
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { GenerationInspectorModal } from '../../components/GenerationInspectorModal';
import { ModalErrorBoundary } from '../../components/ModalErrorBoundary';
import { HoverPlayVideo } from '../../components/HoverPlayVideo';
import {
  AdChromeFrame, AdChromePreview, ChromeSelect,
  normalizeRatio, useAdChrome, useChromeIdentity,
  qcReviewDisplayUrl
} from '../../components/adChrome';
import { funnelStageLabel, mediaTypeLabel } from '../../components/adLabels';
import { safeHttpUrl } from '../../lib/safeHref';
import {
  classifyLatestBatch,
  latestBatchEmptyCopy,
  runTerminalExplanation,
  shouldKeepPolling
} from './runOutcome';

export {
  classifyLatestBatch,
  latestBatchEmptyCopy,
  LATEST_BATCH_EMPTY_FALLBACK,
  LATEST_BATCH_WAITING_COPY,
  runTerminalExplanation,
  shouldKeepPolling
} from './runOutcome';

type AdRow = {
  id:           string;
  brandId:      string;
  campaignId:   string;
  // Every run that selected this Ad. Empty until the Ad is first
  // picked by a CampaignRun; re-render dedupe hits append the new
  // runId so /ads?campaignRunId=X always finds the Ad if it's ever
  // been part of run X.
  campaignRunIds: string[];
  mediaId:      string | null;
  productId:    string | null;
  // The retailer's own product-page URL (CatalogProduct.productUrl),
  // joined + brand-scoped server-side (routes/ads.js projectAd via
  // loadProductUrlMap). null when there's no productId, the product was
  // unlinked/soft-deleted, or the product simply has no URL on file.
  productUrl:   string | null;
  template:     string;
  aspectRatio:  string;
  // Placement the ad was generated for (services/platformFormats.js).
  // Seeds the default chrome in the placement preview.
  platformFormat: string | null;
  matchTier:    'product_match' | 'product_category' | 'brand_match' | 'brand_only';
  variantKind:  'product_image' | 'ugc';
  readinessScore: number | null;
  campaignKind: string | null;
  kind:         'image' | 'video';
  // Intent profile (models/Ad.js funnelStage) — absent on most static ads
  // and on video ads that predate the funnel-stage retitle feature. Render
  // via funnelStageLabel(); absent must show as nothing, never a raw token.
  funnelStage?: 'awareness' | 'consideration' | 'conversion' | null;
  // What the SEED Media was, regardless of what shipped. When kind='image'
  // but sourceFileType='video', the composite failed and the ad fell back
  // to static — image-ref polish correctly skips video sources, so polish
  // never lands and POLISHING badge would otherwise stick forever.
  sourceFileType?: 'image' | 'video' | null;
  // Fire-and-forget pipeline progress stamp (services/adStage.js via
  // projectAd). On video ads the backend flips status→'draft' the instant
  // the paid master lands, then keeps stamping renderStage through titling;
  // terminal value is 'done'. Null/absent on older ads — treat as "no
  // progress signal" (plain status badge). Image ads stamp this too; the
  // old badge helper dropped them.
  renderStage?: string | null;
  // When the backend last flipped renderStage. Drives the badge elapsed
  // clock; absent on older rows (and until the API started serialising it).
  renderStageAt?: string | null;
  // Render-output fields null until status transitions to 'draft' or beyond.
  renderUrl:    string | null;
  // Downscaled Cloudinary variant of the same video master, emitted by the
  // backend for grid tiles. Absent on older payloads — always fall back.
  previewVideoUrl?: string | null;
  // Operator-facing failure headline, present only when status==='failed'.
  // Leads with the policy label (e.g. "Model Moderation Error: Input Prompt
  // violates policy"), so a content rejection is distinguishable from an
  // infrastructure fault the operator might otherwise retry forever.
  renderErrorMessage?: string | null;
  // 'charged' | 'not-charged' | 'unknown' — whether the failed attempt cost us.
  chargeState?: string | null;
  // Phase B — gpt-image-1 polished version, joined from AiFullRender-
  // Artifact by the Ad's cache key. Frontend displays this instead of
  // renderUrl when useImageRefAsProduction is true AND populated.
  photorealUrl: string | null;
  useImageRefAsProduction: boolean;
  posterUrl:    string | null;
  width:        number | null;
  height:       number | null;
  bytes:        number | null;
  copy: {
    headline?:     string;
    cta_text?:     string;
    quote?:        string;
    productName?:  string;
    productPrice?: string;
  };
  ctaText:      string;
  ctaUrl:       string;
  ctaUrlParams: string;
  status:       'queued' | 'rendering' | 'draft' | 'live' | 'archived' | 'failed';
  queuedAt:     string | null;
  renderedAt:   string | null;
  generatedAt:  string;
  // Meta Ads sync — populated once the operator pushes the ad to a
  // connected Meta ad account. Drives the per-card "Synced to Meta"
  // / "Push failed" pill and the detail-modal "View on Meta" link.
  metaAdId:           string | null;
  metaAdCreativeId:   string | null;
  metaAdsetId:        string | null;
  metaCampaignId:     string | null;
  metaAdAccountId:    string | null;
  metaSyncStatus:     'synced' | 'failed' | null;
  metaSyncError:      string | null;
  metaSyncedAt:       string | null;
  // Compact vision-QC verdict — services/adVisionQcService.js
  // summarizeVisionQc, via projectAd(). null when the ad predates the QC
  // gate (no verdict was ever written). `inspected` is the field to gate
  // display on: a `skipped` (flag on, inspection failed to run) or
  // `disabled` (flag off) verdict still returns an object here, but with
  // `passed:false` always — never read those as "this shipped clean".
  // categories/findings are present only when the backend served the
  // detail (`full`) projection, which this list fetch does not request —
  // present for forward compat with any full-fetch consumer.
  visionQc?: {
    inspected:    boolean;
    passed:       boolean;
    skipped:      boolean;
    disabled:     boolean;
    reason:       string | null;
    finalAttempt: number | null;
    regenerated:  boolean;
    summary:      string | null;
    categories?: Record<string, { score: number | null; pass: boolean; findings: string[] }>;
    // Last vision-QC attempt's own candidate. Shared helper
    // qcReviewDisplayUrl falls back to this when photorealUrl/renderUrl
    // are null. Type only — this page does NOT mount QcFailureReview
    // (legacy gallery; owner: skip the button here).
    discardedRenderUrl?: string | null;
  } | null;
};

// Display source. photorealUrl is preferred when present, which now only
// happens on ads rendered BEFORE the direct_image rewrite (2026-07-31) — the
// shadow that produced it was deleted, so new ads always show renderUrl, and
// that IS the finished ad rather than an intermediate seed.
//
// Video ads always use renderUrl (the Cloudinary composite): the polish never
// produced video, so a stale photoreal PNG in a <video> tag wouldn't play.
// QC-failed ads fall back to visionQc.discardedRenderUrl via the shared
// helper (same rule ProductAds displayUrlFor uses). Type/utility coverage
// only — this legacy page does not mount the override button.
function displayUrl(ad: AdRow): string | null {
  return qcReviewDisplayUrl(ad);
}

// Grid-tile source. Same asset as displayUrl, but the backend's downscaled
// ~480px variant when it offered one, so a gallery of N video ads doesn't
// stream N full-bitrate 1080p masters for tiles a few hundred pixels wide.
// The DETAIL view deliberately keeps using displayUrl — full quality there.
// Falls back to displayUrl for image ads, for older payloads with no
// previewVideoUrl, and whenever the backend couldn't transform the URL.
function gridDisplayUrl(ad: AdRow): string | null {
  if (ad.kind === 'video' && ad.previewVideoUrl) return ad.previewVideoUrl;
  return displayUrl(ad);
}

function isShowingPhotoreal(ad: AdRow): boolean {
  return ad.kind !== 'video' && !!ad.photorealUrl;
}

export type AdStatusBadgeTone = 'live' | 'progress' | 'warn' | 'idle';

export type AdStatusBadge = {
  label: string;
  detail: string | null;
  inProgress: boolean;
  tone: AdStatusBadgeTone;
};

type StageRule = {
  test: string | RegExp;
  label: string;
  tone: AdStatusBadgeTone;
};

// Adding a backend stage is one line. First match wins — keep more
// specific prefixes above their general siblings (vision QC regen before
// vision QC, face-safe crop skipped before face-safe crop).
//
// Matching is kind-agnostic on purpose. The previous helper gated on
// `kind==='video' && status==='draft'` and mapped
// "queued for titling (3 ahead)" → "Queued", which is the measured
// defect: image ads and pre-master video showed a bare status, and the
// titling queue hid the only number the operator actually needs.
const STAGE_RULES: StageRule[] = [
  // Video
  { test: 'queued for titling',                 label: 'Waiting to title',       tone: 'progress' },
  { test: 'titling recovered master',           label: 'Titling',                tone: 'progress' },
  { test: /^titling\b/,                         label: 'Titling',                tone: 'progress' },
  { test: 'face-safe crop skipped',             label: 'Crop skipped',           tone: 'progress' },
  { test: 'face-safe crop',                     label: 'Cropping',               tone: 'progress' },
  { test: 'uploading titled video',             label: 'Uploading',              tone: 'progress' },
  { test: 'no titling',                         label: 'Finishing',              tone: 'progress' },
  { test: 'master video generation',            label: 'Generating video',       tone: 'progress' },
  { test: 'master video submit',                label: 'Submitting video',       tone: 'progress' },
  { test: 'preparing video context',            label: 'Preparing',              tone: 'progress' },
  { test: 'reference reframe',                  label: 'Reframing',              tone: 'progress' },
  { test: 'downloading master video',           label: 'Downloading',            tone: 'progress' },
  { test: 'mirror upload',                      label: 'Uploading',              tone: 'progress' },
  { test: 'derive-only: master wait exhausted', label: 'Master wait exhausted',  tone: 'warn' },
  { test: 'derive-only: master missing/failed', label: 'Master missing',         tone: 'warn' },
  { test: 'derive-only wait for master',        label: 'Waiting for master',     tone: 'progress' },
  { test: 'derive-only: waiting for master',    label: 'Waiting for master',     tone: 'progress' },
  { test: 'derive-only plate from',             label: 'Deriving plate',         tone: 'progress' },
  { test: 'derived plate ready; titling failed',label: 'Titling failed',         tone: 'warn' },
  { test: 'master rendered; titling failed',    label: 'Titling failed',         tone: 'warn' },
  { test: 'master recovered; titling pending',  label: 'Titling pending',        tone: 'progress' },
  { test: 'ugc-video mirrored + passthrough',   label: 'Passing through',        tone: 'progress' },
  { test: 'ugc-video passthrough',              label: 'Passing through',        tone: 'progress' },
  { test: 'reusing video seed segment',         label: 'Reusing seed',           tone: 'progress' },
  // Scene reel — adgen's renderer, API contract §7. Every stage starts with
  // 'scene reel: ', so none of these can shadow a rule above or below. Most
  // specific first within the family: 'frame qc' before 'frames', and the
  // per-scene qc / retry / failed before the 'scenes k/4' pair. The k/4 and
  // n/N counts land in the badge detail (SCENE_PROGRESS_RE). Once the reel is
  // assembled the titling rules above take over.
  { test: 'scene reel: selecting images',       label: 'Selecting images',       tone: 'progress' },
  { test: 'scene reel: planning',               label: 'Planning scenes',        tone: 'progress' },
  { test: 'scene reel: frame qc',               label: 'Checking frames',        tone: 'progress' },
  { test: 'scene reel: frames',                 label: 'Preparing frames',       tone: 'progress' },
  { test: 'scene reel: scene qc',               label: 'Checking scene',         tone: 'progress' },
  { test: 'scene reel: scene retry',            label: 'Retrying scene',         tone: 'progress' },
  { test: 'scene reel: scene failed',           label: 'Scene failed',           tone: 'warn' },
  { test: /^scene reel: scenes\b.*\bsubmit\b/,   label: 'Submitting scenes',      tone: 'progress' },
  { test: /^scene reel: scenes\b.*\bgeneration\b/, label: 'Generating scenes',    tone: 'progress' },
  { test: 'scene reel: assembling',             label: 'Assembling reel',        tone: 'progress' },
  { test: 'scene reel: cropping 1:1',           label: 'Cropping 1:1',           tone: 'progress' },
  // The cut wait: the master that finishes its scenes first is released until
  // the other master's takes are in, so both cut the same story. The 9:16
  // waits for the 16:9; the 16:9 (and the 1:1) wait for the 9:16.
  { test: 'scene reel: waiting for 9:16',       label: 'Waiting for 9:16',       tone: 'progress' },
  { test: 'scene reel: waiting for 16:9',       label: 'Waiting for 16:9',       tone: 'progress' },
  // Static
  { test: 'deriving layout',                    label: 'Planning layout',        tone: 'progress' },
  { test: 'fetching references',                label: 'Fetching references',    tone: 'progress' },
  { test: 'building prompt + geometry',         label: 'Building prompt',        tone: 'progress' },
  { test: 'plate submit',                       label: 'Generating image',       tone: 'progress' },
  { test: 'static image generation',            label: 'Generating image',       tone: 'progress' },
  { test: 'plate generation',                   label: 'Generating image',       tone: 'progress' },
  { test: 'crop + logo composite',              label: 'Compositing',            tone: 'progress' },
  { test: 'vision QC regen',                    label: 'Quality check retry',    tone: 'progress' },
  { test: 'vision QC',                          label: 'Quality check',          tone: 'progress' },
  { test: 'uploading static',                   label: 'Uploading',              tone: 'progress' },
  { test: 'persisting static',                  label: 'Saving',                 tone: 'progress' },
  { test: 'recovered from receipt',             label: 'Recovered',              tone: 'progress' },
  { test: /^skipped:/,                          label: 'Skipped',                tone: 'idle' },
];

const POLL_TRAILER_RE = /\s+[—–-]\s+polling\s+(.+)$/i;
const RETRY_TRAILER_RE = /\s+[—–-]\s+retry\s+(\d+)\s*$/i;
const QUEUE_AHEAD_RE = /\((\d+)\s+ahead\)/i;
const ATTEMPT_RE = /\(attempt\s+(\d+)\s*\/\s*(\d+)\)/i;
const SKIPPED_RE = /^skipped:\s*(.+)$/i;
// Scene reel counts: 'scene reel: frames 3/8', 'scene reel: scenes 2/4 submit'
// → "3/8"; 'scene reel: scene qc 2' → "scene 2". Anchored on the family prefix,
// so no other stage can produce either detail.
const SCENE_PROGRESS_RE = /^scene reel:\s*(?:frames|scenes)\s+(\d+)\s*\/\s*(\d+)/i;
const SCENE_INDEX_RE = /^scene reel:\s*scene\s+(?:qc|retry|failed)\s+(\d+)/i;
const WARN_STAGE_RE = /\b(failed|exhausted|missing)\b/i;
const ELAPSED_FLOOR_MS = 30_000;

// BOTH SIDES ARE LOWERCASED, and that is load-bearing rather than defensive.
//
// Matching runs against `stage.toLowerCase()` so a backend casing change cannot
// break a label. But the table is written to be READ, so it carries the
// backend's real casing — 'vision QC', not 'vision qc'. Comparing a lowercased
// stage against a mixed-case test means those rules can NEVER fire, and the
// failure is silent: the stage falls through to cleanUnknownStage, which
// produces something readable ("Vision QC"), so the badge looks right while the
// table is dead. Caught in the harness — 'vision QC (meta_feed_1_1)' rendered
// the cleaned raw string instead of "Quality check".
//
// Normalising the test here keeps the table human-readable AND reachable. The
// alternative — lowercasing every entry by hand — is one typo away from the
// same silent hole.
function ruleMatches(stage: string, test: string | RegExp): boolean {
  if (typeof test === 'string') return stage.startsWith(test.toLowerCase());
  return test.test(stage);
}

// Every rule must be reachable. An unreachable rule is not a dead branch — it is
// a label the operator was promised and will never see, and nothing else would
// report it. Exported so the harness can prove reachability against the real
// backend vocabulary rather than trusting the table's shape.
export function unreachableStageRules(vocabulary: string[]): string[] {
  const lowered = vocabulary.map(s => s.toLowerCase());
  return STAGE_RULES
    .filter(r => !lowered.some(s => ruleMatches(s, r.test)))
    .map(r => (typeof r.test === 'string' ? r.test : String(r.test)));
}

function formatElapsedMs(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m <= 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function joinDetail(parts: Array<string | null | undefined>): string | null {
  const xs = parts.map(p => p?.trim()).filter((p): p is string => !!p);
  return xs.length ? xs.join(' · ') : null;
}

// Unknown stage → real words, not "In Progress". Strip the poll trailer
// and parentheticals (aspect / surface / queue) so the badge stays short.
function cleanUnknownStage(stage: string): string {
  let s = stage.replace(POLL_TRAILER_RE, '').replace(RETRY_TRAILER_RE, '');
  s = s.replace(/\s*\([^)]*\)/g, '');
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) s = stage.trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function extractStageDetail(stage: string): {
  scene: string | null;
  ahead: string | null;
  poll: string | null;
  retry: string | null;
  attempt: string | null;
  skipped: string | null;
} {
  const pollM = stage.match(POLL_TRAILER_RE);
  const retryM = stage.match(RETRY_TRAILER_RE);
  const aheadM = stage.match(QUEUE_AHEAD_RE);
  const attemptM = stage.match(ATTEMPT_RE);
  const skippedM = stage.match(SKIPPED_RE);
  const sceneProgressM = stage.match(SCENE_PROGRESS_RE);
  const sceneIndexM = stage.match(SCENE_INDEX_RE);
  return {
    scene: sceneProgressM ? `${sceneProgressM[1]}/${sceneProgressM[2]}`
      : sceneIndexM ? `scene ${sceneIndexM[1]}`
      : null,
    ahead: aheadM ? `${aheadM[1]} ahead` : null,
    poll: pollM ? pollM[1].trim() : null,
    retry: retryM ? `retry ${retryM[1]}` : null,
    attempt: attemptM ? `attempt ${attemptM[1]}/${attemptM[2]}` : null,
    skipped: skippedM ? skippedM[1].trim() : null,
  };
}

function elapsedDetail(renderStageAt: string | null | undefined, now: number): string | null {
  if (!renderStageAt) return null;
  const t = Date.parse(renderStageAt);
  if (!Number.isFinite(t)) return null;
  const ms = now - t;
  if (ms <= ELAPSED_FLOOR_MS) return null;
  return formatElapsedMs(ms);
}

export function adStatusBadgeColorScheme(tone: AdStatusBadgeTone): string {
  switch (tone) {
    case 'live':     return 'green';
    case 'progress': return 'cyan';
    case 'warn':     return 'orange';
    case 'idle':     return 'gray';
  }
}

/**
 * Card status-badge for image and video ads. Prefers a live `renderStage`
 * over the coarse `status` so the operator sees the pipeline step, never
 * a bare "Queued" while the backend already knew the answer.
 *
 * Pure + exported for unit tests. `now` is injectable so elapsed assertions
 * don't depend on the wall clock.
 */
export function resolveAdStatusBadge(
  status: string,
  kind: 'image' | 'video',
  renderStage: string | null,
  renderStageAt?: string | null,
  now?: number
): AdStatusBadge {
  // kind is on the public contract so both galleries stay branch-free.
  // It must not gate matching — that was the measured defect.
  void kind;

  const clock = now ?? Date.now();
  const stage = renderStage?.trim() || '';
  const stageKey = stage.toLowerCase();

  if (!stage || stageKey === 'done') {
    return {
      label: status,
      detail: null,
      inProgress: false,
      // failed is warn so a dead render doesn't recede into idle gray;
      // everything else without a live stage follows the spec (live vs idle).
      tone: status === 'live' ? 'live' : status === 'failed' ? 'warn' : 'idle',
    };
  }

  const rule = STAGE_RULES.find(r => ruleMatches(stageKey, r.test));
  let label = rule ? rule.label : cleanUnknownStage(stage);
  let tone: AdStatusBadgeTone = rule ? rule.tone : 'progress';

  if (status === 'failed' || WARN_STAGE_RE.test(stageKey)) tone = 'warn';

  // Safety net: a new "queued …" stage that misses the table must still
  // never collapse to the bare word the operator hates.
  if (/^queued$/i.test(label)) label = 'Waiting';

  const bits = extractStageDetail(stage);
  const elapsed = elapsedDetail(renderStageAt, clock);
  // Poll trailer already carries its own elapsed + attempt count.
  const detail = joinDetail([
    bits.scene,
    bits.ahead,
    bits.poll,
    bits.retry,
    bits.attempt,
    bits.skipped,
    bits.poll ? null : elapsed,
  ]);

  return {
    label,
    detail,
    inProgress: tone === 'progress',
    tone,
  };
}

/**
 * Label a run-level stage BUCKET (from GET /api/ads/runs/:id `stages`, which
 * is services/adStage.js summarizeInFlightStages grouped server-side by
 * stageBase) using the exact same table an individual ad card's badge uses
 * (STAGE_RULES above). One taxonomy for both views on purpose — so the run
 * poller's aggregate "now:" line (mirroring what Slack's live feed already
 * shows for the same run) never disagrees with what a card's own badge calls
 * that stage.
 */
export function stageBucketLabel(rawStage: string): { label: string; tone: AdStatusBadgeTone } {
  const stage = rawStage.trim();
  const stageKey = stage.toLowerCase();
  const rule = STAGE_RULES.find(r => ruleMatches(stageKey, r.test));
  const label = rule ? rule.label : cleanUnknownStage(stage);
  const tone: AdStatusBadgeTone = rule ? rule.tone : (WARN_STAGE_RE.test(stageKey) ? 'warn' : 'progress');
  return { label, tone };
}

type MetaAdsetRow = {
  adsetId:        string;
  adsetName:      string;
  adsetStatus:    string | null;
  campaignId:     string;
  campaignName:   string;
  campaignStatus: string | null;
};

type PushResult = {
  pushed: number;
  failed: number;
  perAd:  Array<{ adId: string; ok: boolean; metaAdId?: string; error?: string }>;
};

type AdsResponse = { ads: AdRow[]; total: number; limit: number; offset: number };

export type RunStatus = {
  runId:           string;
  brandId:         string;
  campaignId:      string;
  total:           number;
  succeeded:       number;
  skipped:         number;
  failed:          number;
  status:          'preparing' | 'running' | 'done' | 'failed';
  queuedRemaining: number;
  // Expand-empty runs stamp { index, stage:'expand', message } with no
  // template/aspectRatio — those fields are render-row diagnostics.
  errors:          Array<{ index: number; stage: string; template?: string; aspectRatio?: string; message: string }>;
  startedAt:       string;
  completedAt:     string | null;
  // Present once claim has run — same { code, message, ... } shape the
  // POST /api/ads/generate 202 uses. 'minted-ads-unclaimed' means this run's
  // mint outgrew what it claimed (a queued backlog was left behind);
  // 'concurrent-run-shares-products' is the informational overlap notice.
  // Null/absent when there's nothing to say.
  notice?: { code?: string; message?: string } | null;
  // Total ads minted for this run's expansion — can exceed `total` (what
  // THIS run actually claimed/rendered) when the mint outgrew this run's
  // claim, leaving leftovers queued for a later "Generate more".
  mintedTotal?: number;
  // Of THIS run's mint, how many were left queued at claim time (the
  // backend's set difference — NOT mintedTotal - total, which double-counts
  // old leftovers the claim absorbed). Snapshot at claim; a later
  // "Generate more" may have drained them since.
  unclaimedAtStart?: number;
  // What stage is the in-flight (status:'rendering') work in RIGHT NOW,
  // grouped and counted. services/adStage.js summarizeInFlightStages —
  // the same base-stage grouping runFeedService already computes for
  // Slack's live "now: …" line, now available to this poller too. Absent
  // (not just empty) on an older backend; empty once nothing is rendering.
  stages?: Array<{ stage: string; count: number }>;
  // Grouped, deduped failure reasons — services/runFeedService.js
  // summariseFailures, the SAME function Slack's run-finished summary
  // calls, reused here so this poller and the Slack channel never describe
  // a failure differently. Present once run.failed > 0, even mid-run (a
  // partial failure, e.g. one shared video master, must not wait for the
  // whole run to finish before it is explained).
  failureSummary?: Array<{ reason: string; count: number }>;
  // Last time the render loop proved it was actually alive and had real
  // in-flight work (services/campaignRunHeartbeat.js, PR #220) — NOT a
  // completion notification. Null while 'preparing' (that lifecycle never
  // heartbeats by design) and while 'running' with nothing currently
  // dispatched. Mongoose `updatedAt` alongside it for an older run or an
  // instant right at claim, before the first beat has landed.
  lastHeartbeatAt?: string | null;
  updatedAt?: string | null;
  // Run-level vision-QC rollup — routes/ads.js GET /runs/:runId, backed by
  // Ad.visionQc via services/adVisionQcService.js summarizeVisionQc. Absent
  // (not just zero) on an older backend — that's "unknown", not "all clean",
  // so treat the two as visually equivalent (render nothing) but never infer
  // a clean batch from an absent field.
  //
  // FIXED 2026-08-19 (production incident: a 39-ad run had visionQc:null on
  // every ad — the flag was off and nobody had ever turned it on — and
  // shippedWithoutQc read as 0 because the backend query only counted an
  // explicit `visionQc.skipped:true`, never a bare absent field). It now
  // counts BOTH, which is the whole point of this rollup: "not inspected"
  // must be visible whether QC consciously declined an ad or a render
  // pipeline never reached QC at all — both look identical to an operator
  // and both need the same warning.
  visionQcRollup?: {
    // NOT INSPECTED — either an explicit skipped/disabled verdict, or (the
    // common case whenever the flag is off) a bare absent visionQc field.
    shippedWithoutQc: number;
    // INSPECTED and FLAGGED — a real vision-model verdict ran and failed.
    // The third state alongside shippedWithoutQc (not inspected) and a
    // clean pass (silent, same "surface the exceptional case" precedent
    // used everywhere else on this page).
    qcFailed: number;
    // Passed, but only after the one allowed regeneration — informational,
    // a subset of "inspected and passed", not its own top-level state.
    qcdOnRetry: number;
  };
};

const POLL_INTERVAL_MS = 2500;
// How long with no update from the render loop before the UI says so — this
// is NOT a reason to stop polling, and it used to be (see POLL_HARD_STOP_MS
// below). A serialized video batch legitimately runs 25-35+ minutes (titling
// serialises behind REMOTION_QUEUE_CONCURRENCY; a single Omni master can
// itself poll for up to 10 minutes before its own provider-side timeout), so
// a fixed "10 minutes since I started watching" client timer is not a
// truthful stall signal — it is just how long this browser tab has been
// open. MEASURED regression from treating it as one anyway
// (run_1787119100250_eef4d871, 2026-08-18): at the 10-minute mark the page
// switched to a permanent "Timed out" card with no progress bar, no percent,
// and a frozen "18 of 39" — while a PMax master was still genuinely
// rendering and the run was very much alive. This constant is now only a
// soft, additive caution threshold; see the `lastHeartbeatAt`-based
// silence check inside RunProgress for the actual (server-truth) signal.
const POLL_SLOW_AFTER_MS = 3 * 60 * 1000;
// Pure safety valve so a browser tab left open forever does not poll
// forever — NOT a verdict on the run. Set comfortably past the backend's own
// worst case: services/campaignRunHeartbeat.js caps a beating run's lifetime
// at 4h (RUN_HEARTBEAT_MAX_MS), after which the reaper takes it to 'failed'
// within another REAP_STALE_MIN (~15m). By 6h the backend has always already
// resolved the run one way or another; this only stops this tab from asking.
const POLL_HARD_STOP_MS = 6 * 60 * 60 * 1000;

type CampaignOption = { id: string; name: string };

export function AdsPage() {
  const { activeBrand } = useBrand();
  const activeBrandId = activeBrand?.id || null;
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const campaignRunId = params.get('campaignRunId');
  const campaignId    = params.get('campaignId');
  // The run is keyed { runId, brandId } server-side. /generate hands back the
  // run's OWN brandId and the wizard forwards it here, because polling with the
  // globally-selected brand 404s whenever the campaign belongs to a different
  // brand. Falls back to the active brand for links minted before this existed.
  const runBrandId    = params.get('runBrandId');

  const [status, setStatus] = useState<string>('draft');
  const [rows, setRows]     = useState<AdRow[]>([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState<string | null>(null);
  // Progress-poll failures are tracked SEPARATELY from ad-list failures. They
  // used to share one state, so a run lookup that 404'd rendered as
  // "Failed to load ads: run not found" — alarming, and wrong: the ads had
  // loaded fine and were still rendering server-side.
  const [runErr, setRunErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdRow | null>(null);

  // Placement chrome for the detail modal. Hooks run unconditionally,
  // so this resolves against a square default while nothing is open and
  // re-resolves when the operator opens an ad.
  const selectedRatio = normalizeRatio(selected?.aspectRatio, selected?.width, selected?.height);
  const selectedProductHref = safeHttpUrl(selected?.productUrl);
  const chromeIdentity = useChromeIdentity({
    ctaText: selected?.copy.cta_text || selected?.ctaText || null
  });
  const {
    chrome: detailChrome,
    setChrome: setDetailChrome,
    options: chromeOptions
  } = useAdChrome(selectedRatio, selected?.platformFormat);
  const [run, setRun]       = useState<RunStatus | null>(null);
  // True only once auto-refresh has actually GIVEN UP (POLL_HARD_STOP_MS) —
  // an abandoned-tab safety valve, not a verdict on the run. Distinct from
  // "slow": a run can be perfectly healthy and just taking a long time.
  const [pollStopped, setPollStopped] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const detailModal = useDisclosure();
  // Generation inspector — sibling modal (same pattern as ProductAds
  // AdDetailModal). Gated on selected.id + activeBrandId.
  const [showInspector, setShowInspector] = useState(false);

  // Meta-push selection + modal state. selectedIds holds the ad ids
  // the operator has ticked for batch push; the toolbar appears when
  // the set is non-empty. metaAdsets is fetched lazily on modal open.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [metaAdsets, setMetaAdsets]   = useState<MetaAdsetRow[]>([]);
  const [adsetsLoading, setAdsetsLoading] = useState(false);
  const [chosenAdsetId, setChosenAdsetId] = useState<string>('');
  const [pushing, setPushing] = useState(false);
  const pushModal = useDisclosure();

  function toggleSelect(adId: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(adId)) next.delete(adId); else next.add(adId);
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }

  // Fetch the brand's Meta AdSets when the push modal opens. Always
  // fresh (don't cache across opens) so a newly-synced AdSet shows
  // up without a page reload.
  const openPushModal = async () => {
    if (!activeBrandId) return;
    setChosenAdsetId('');
    setMetaAdsets([]);
    pushModal.onOpen();
    setAdsetsLoading(true);
    try {
      const res = await apiJson<{ adsets: MetaAdsetRow[] }>(
        `/api/ads/meta-adsets?brandId=${encodeURIComponent(activeBrandId)}`
      );
      setMetaAdsets(res.adsets || []);
    } catch (e) {
      toast({
        title:       'Could not load Meta AdSets',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setAdsetsLoading(false);
    }
  };

  const submitPush = async () => {
    if (!activeBrandId || !chosenAdsetId || selectedIds.size === 0) return;
    setPushing(true);
    try {
      const adIds = Array.from(selectedIds);
      const result = await apiJson<PushResult>('/api/ads/push-to-meta', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ brandId: activeBrandId, adsetId: chosenAdsetId, adIds })
      });
      const summary = result.failed === 0
        ? `Pushed ${result.pushed} ad${result.pushed === 1 ? '' : 's'} as PAUSED.`
        : `Pushed ${result.pushed}, failed ${result.failed}. Check the failed pills for the error.`;
      toast({
        title:       result.failed === 0 ? 'Synced to Meta' : 'Pushed with errors',
        description: summary,
        status:      result.failed === 0 ? 'success' : 'warning',
        duration:    5000
      });
      pushModal.onClose();
      clearSelection();
      // Refresh the list so the new metaSyncStatus pills show up.
      await fetchRef.current?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({
        title:       'Push failed',
        description: msg,
        status:      'error',
        duration:    7000
      });
    } finally {
      setPushing(false);
    }
  };

  // Group AdSets by campaign for the dropdown rendering.
  const adsetsByCampaign = useMemo(() => {
    const groups = new Map<string, { campaignName: string; adsets: MetaAdsetRow[] }>();
    for (const a of metaAdsets) {
      const k = a.campaignId;
      if (!groups.has(k)) groups.set(k, { campaignName: a.campaignName, adsets: [] });
      groups.get(k)!.adsets.push(a);
    }
    return Array.from(groups.values());
  }, [metaAdsets]);

  // Pull the brand's campaigns once per active-brand change for the
  // campaign filter dropdown. Failure is silent — the dropdown just
  // shows "All campaigns" and the URL-driven campaignId still works.
  useEffect(() => {
    if (!activeBrandId) { setCampaigns([]); return; }
    let cancelled = false;
    apiJson<{ campaigns: CampaignOption[] }>(`/api/campaigns?brandId=${encodeURIComponent(activeBrandId)}`)
      .then(res => { if (!cancelled) setCampaigns(res.campaigns || []); })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, [activeBrandId]);

  const setCampaignFilter = (nextCampaignId: string) => {
    const next = new URLSearchParams(params);
    if (nextCampaignId) next.set('campaignId', nextCampaignId);
    else                next.delete('campaignId');
    // Changing the campaign filter invalidates an open run scope —
    // a run belongs to one campaign, so cross-campaign filtering
    // shouldn't keep the run-tied view in the URL.
    next.delete('campaignRunId');
    next.delete('runBrandId');   // travels with campaignRunId; never outlives it
    setParams(next, { replace: false });
  };

  const activeCampaignName = useMemo(
    () => campaigns.find(c => c.id === campaignId)?.name || null,
    [campaigns, campaignId]
  );

  // Reset run state when the campaignRunId param changes (e.g. user
  // bookmarks a different run, or navigates back to /ads with no run).
  useEffect(() => {
    setRun(null);
    setPollStopped(false);
    setRunErr(null);
  }, [campaignRunId]);

  // Ad list fetcher. Memoized into a stable callback via ref so the
  // poller can re-trigger it without re-running this effect.
  const fetchRef = useRef<() => Promise<void>>();

  // Mirror `rows` into a ref so the run poller can check awaiting-
  // polish state from inside its tick closure without forcing the
  // effect to re-subscribe on every row update. The poller keeps
  // ticking while ANY visible row is still waiting on its photoreal
  // — image-ref runs fire-and-forget post-persist and frequently
  // lands AFTER the run.status flips to 'done', so a poll that
  // stopped purely on run.status left photorealUrl updates stranded
  // and the operator had to manually refresh to see the polished
  // image (renderUrl → photorealUrl swap never happened in-session).
  const rowsRef = useRef<AdRow[]>([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => {
    fetchRef.current = async () => {
      if (!activeBrandId) return;
      const qp = new URLSearchParams({ brandId: activeBrandId, limit: '120' });
      if (status)          qp.set('status', status);
      if (campaignId)      qp.set('campaignId', campaignId);
      // DB-level scope, not just a client-side filter over a capped page —
      // a dedupe-reused ad's generatedAt never updates on re-render, so a
      // fresh batch could otherwise sort/page outside the fetched window
      // even when properly scoped by campaignId alone. See routes/ads.js's
      // GET /api/ads campaignRunId filter.
      if (campaignRunId)   qp.set('campaignRunId', campaignRunId);
      try {
        const res = await apiJson<AdsResponse>(`/api/ads?${qp.toString()}`);
        // Kept as a harmless belt-and-braces no-op now that the server
        // already enforces the same scope via campaignRunId above.
        const filtered = campaignRunId
          ? res.ads.filter(a => Array.isArray(a.campaignRunIds) && a.campaignRunIds.includes(campaignRunId))
          : res.ads;
        setRows(filtered);
        setTotal(filtered.length);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    };
  }, [activeBrandId, status, campaignId, campaignRunId]);

  // Initial / param-change ad list load.
  useEffect(() => {
    if (!activeBrandId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      await fetchRef.current?.();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activeBrandId, status, campaignId, campaignRunId]);

  // ── Status actions (Approve / Archive / Restore / Delete) ──
  // Optimistically updates local state, fires PATCH/DELETE, rolls
  // back on failure with a toast. Avoids a full gallery refetch
  // because the gallery is already showing the row we mutated.

  const [actioning, setActioning] = useState(false);

  // Per-ad title copy edit + re-render (detail modal). Draft fields track
  // the selected ad. Re-render is runnable standalone (no prior save gate).
  const [editHeadline, setEditHeadline] = useState('');
  const [editCta, setEditCta] = useState('');
  const [copySaving, setCopySaving] = useState(false);
  const [titleRerendering, setTitleRerendering] = useState(false);
  const [renderBust, setRenderBust] = useState(0);
  // Last-seen prop copy — prop-sync only overwrites drafts when the
  // operator has no unsaved local edits vs the previous server values.
  const prevCopyPropsRef = useRef<{ headline: string; cta_text: string }>({
    headline: '',
    cta_text: ''
  });
  // Live open-ad id for stale-response guards.
  const openAdIdRef = useRef<string | null>(null);
  openAdIdRef.current = selected?.id ?? null;

  // Reset draft only when switching ads. Keep as-is (full reset).
  useEffect(() => {
    if (!selected) return;
    setEditHeadline(selected.copy.headline || '');
    setEditCta(selected.copy.cta_text || '');
    setRenderBust(0);
    prevCopyPropsRef.current = {
      headline: selected.copy.headline || '',
      cta_text: selected.copy.cta_text || ''
    };
  }, [selected?.id]);

  // Prop-sync: only when drafts equal previous prop values (no local edits).
  useEffect(() => {
    if (!selected) return;
    const prev = prevCopyPropsRef.current;
    const noLocalEdits =
      editHeadline.trim() === prev.headline.trim() &&
      editCta.trim() === prev.cta_text.trim();
    if (noLocalEdits) {
      setEditHeadline(selected.copy.headline || '');
      setEditCta(selected.copy.cta_text || '');
    }
    prevCopyPropsRef.current = {
      headline: selected.copy.headline || '',
      cta_text: selected.copy.cta_text || ''
    };
    // Intentionally omit editHeadline/editCta: evaluate dirtiness only when
    // server props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.copy.headline, selected?.copy.cta_text]);

  const saveSelectedCopy = async () => {
    if (!activeBrandId || !selected || copySaving) return;
    const startedAdId = selected.id;
    const headline = editHeadline.trim().slice(0, 300);
    const cta_text = editCta.trim().slice(0, 300);
    const prevH = (selected.copy.headline || '').trim();
    const prevC = (selected.copy.cta_text || '').trim();
    const copy: { headline?: string | null; cta_text?: string | null } = {};
    if (headline !== prevH) copy.headline = headline || null;
    if (cta_text !== prevC) copy.cta_text = cta_text || null;
    if (Object.keys(copy).length === 0) return;

    setCopySaving(true);
    try {
      await apiJson<{ ad: AdRow }>(
        `/api/ads/${startedAdId}?brandId=${encodeURIComponent(activeBrandId)}`,
        {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ copy })
        }
      );
      // Stale-response guard: operator switched ads mid-flight.
      if (openAdIdRef.current !== startedAdId) return;
      const nextCopy = {
        ...selected.copy,
        ...(copy.headline !== undefined ? { headline: copy.headline ?? undefined } : {}),
        ...(copy.cta_text !== undefined ? { cta_text: copy.cta_text ?? undefined } : {})
      };
      const nextSelected = { ...selected, copy: nextCopy };
      setSelected(nextSelected);
      setRows(prev => prev.map(r => r.id === startedAdId ? { ...r, copy: nextCopy } : r));
      toast({ title: 'Copy saved', status: 'success', duration: 2500 });
    } catch (e) {
      if (openAdIdRef.current !== startedAdId) return;
      toast({
        title:       'Copy save failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setCopySaving(false);
    }
  };

  const rerenderSelectedTitle = async () => {
    if (!activeBrandId || !selected || titleRerendering || selected.kind !== 'video') return;
    const startedAdId = selected.id;
    const brandId = activeBrandId;
    setTitleRerendering(true);
    try {
      // Live retitle is async (adgen job). POST 202 {jobId}; poll GET until
      // status is done/failed. Ineligible ads still 202 — errors land on the job.
      const kick = await apiJson<{
        jobId?: string;
        status?: string;
        count?: number;
        error?: string;
        errors?: Array<{ id: string; error: string }>;
      }>(`/api/brand/${encodeURIComponent(brandId)}/retitle-videos`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ adIds: [startedAdId] })
      });
      if (openAdIdRef.current !== startedAdId) return;
      const jobId = kick.jobId;
      if (!jobId) {
        const syncErr = kick.error
          || kick.errors?.find(e => e.id === startedAdId)?.error
          || kick.errors?.[0]?.error
          || 'Title re-render did not start';
        throw new Error(syncErr);
      }

      type RetitlePoll = {
        status: 'pending' | 'running' | 'done' | 'failed' | string;
        count?: number;
        progress?: { done: number; total: number };
        results?: Array<{ id: string; ok: boolean; renderUrl?: string | null; error?: string }>;
        errors?: Array<{ id: string; error: string }>;
        error?: string;
        elapsedMs?: number;
      };
      const INTERVAL_MS = 2500;
      const TIMEOUT_MS  = 5 * 60 * 1000;
      const startedAt = Date.now();
      const job = await new Promise<RetitlePoll | null>((resolve, reject) => {
        const tick = async () => {
          if (openAdIdRef.current !== startedAdId) {
            resolve(null);
            return;
          }
          try {
            const s = await apiJson<RetitlePoll>(
              `/api/brand/${encodeURIComponent(brandId)}/retitle-videos/${encodeURIComponent(jobId)}`
            );
            if (s.status === 'done' || s.status === 'failed') {
              resolve(s);
              return;
            }
          } catch (e) {
            // 404 = expired/unknown job (in-memory store); retrying never recovers.
            const status = (e as { status?: number } | undefined)?.status;
            if (status === 404) {
              reject(e instanceof Error ? e : new Error('Retitle job not found or expired'));
              return;
            }
            // Transient failures retry on the next tick (same as pollPreview).
          }
          if (Date.now() - startedAt > TIMEOUT_MS) {
            reject(new Error('Title re-render took longer than 5 minutes — aborted. Try again.'));
            return;
          }
          setTimeout(tick, INTERVAL_MS);
        };
        setTimeout(tick, INTERVAL_MS);
      });
      // Stale-response guard: operator switched ads mid-flight.
      if (!job || openAdIdRef.current !== startedAdId) return;

      if (job.status === 'failed') {
        throw new Error(job.error || 'Title re-render failed');
      }
      const mine = (job.results || []).find(r => r.id === startedAdId);
      if (mine && !mine.ok) {
        throw new Error(mine.error || 'Title re-render failed');
      }
      if (!mine) {
        const seed = (job.errors || []).find(e => e.id === startedAdId);
        throw new Error(seed?.error || job.error || 'This ad was not eligible to re-title');
      }

      const nextUrl = mine.renderUrl ?? null;
      if (nextUrl) {
        const updated = { ...selected, renderUrl: nextUrl };
        setSelected(updated);
        setRows(prev => prev.map(r => r.id === startedAdId ? { ...r, renderUrl: nextUrl } : r));
      }
      // Always cache-bust (backend may rewrite in place under the same URL).
      setRenderBust(Date.now());
      toast({
        title:       'Title re-rendered',
        description: nextUrl
          ? 'Preview updated with the new title copy.'
          : 'Re-render completed; refresh if the preview does not update.',
        status:      'success',
        duration:    3500
      });
    } catch (e) {
      if (openAdIdRef.current !== startedAdId) return;
      toast({
        title:       'Title re-render failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    6000
      });
    } finally {
      setTitleRerendering(false);
    }
  };

  const setAdStatus = async (ad: AdRow, nextStatus: AdRow['status']) => {
    if (!activeBrandId) return;
    setActioning(true);
    const prevRows = rows;
    const prevSelected = selected;
    setRows(prevRows.map(r => r.id === ad.id ? { ...r, status: nextStatus } : r));
    if (prevSelected?.id === ad.id) setSelected({ ...prevSelected, status: nextStatus });
    try {
      await apiJson<{ ad: AdRow }>(`/api/ads/${ad.id}?brandId=${encodeURIComponent(activeBrandId)}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ status: nextStatus })
      });
      toast({
        title:    nextStatus === 'live'     ? 'Ad approved'
                : nextStatus === 'archived' ? 'Ad archived'
                : 'Ad restored to draft',
        status:   'success',
        duration: 2500
      });
    } catch (e) {
      // rollback
      setRows(prevRows);
      if (prevSelected) setSelected(prevSelected);
      toast({
        title:       'Status change failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setActioning(false);
    }
  };

  const deleteAd = async (ad: AdRow) => {
    if (!activeBrandId) return;
    const confirmed = window.confirm('Delete this ad? Removes the doc + the Cloudinary asset. Cannot be undone.');
    if (!confirmed) return;
    setActioning(true);
    const prevRows = rows;
    setRows(prevRows.filter(r => r.id !== ad.id));
    setSelected(null);
    detailModal.onClose();
    try {
      await apiJson(`/api/ads/${ad.id}?brandId=${encodeURIComponent(activeBrandId)}`, { method: 'DELETE' });
      toast({ title: 'Ad deleted', status: 'success', duration: 2500 });
    } catch (e) {
      setRows(prevRows);
      toast({
        title:       'Delete failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setActioning(false);
    }
  };

  // Run-status poller — fires only when the URL has a campaignRunId.
  // Polls /api/ads/runs/:id, refetches the ad list each tick, and
  // stops when status flips to done|failed (or the timeout trips).
  useEffect(() => {
    if (!activeBrandId || !campaignRunId) return;
    let cancelled = false;
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;
    // A run lookup can miss transiently (or permanently, if the link carries no
    // runBrandId and the switcher is on another brand). Either way the ads
    // themselves are fine, so tolerate a few misses before giving up and never
    // let this stop the ad list from refreshing.
    let consecutiveFailures = 0;
    const MAX_RUN_POLL_FAILURES = 3;
    const pollBrandId = runBrandId || activeBrandId;

    const tick = async () => {
      if (cancelled) return;
      try {
        const next = await apiJson<RunStatus>(
          `/api/ads/runs/${encodeURIComponent(campaignRunId)}?brandId=${encodeURIComponent(pollBrandId)}`
        );
        if (cancelled) return;
        consecutiveFailures = 0;
        setRunErr(null);
        setRun(next);
        await fetchRef.current?.();
        // The old second condition — "or any visible ad is still awaiting its
        // photoreal polish" — is GONE with the polish step itself. Keeping it
        // would have meant polling forever: photorealUrl can no longer be set,
        // so the predicate never went false.
        //
        // 'preparing' = expandWizardJob is still running in the background
        // (Director + Judge ~15-25s). Keep polling until status flips to
        // 'running' (total is set) or 'done'/'failed' (terminal).
        //
        // total===0 is NOT a stop condition — every preparing run has
        // total 0 until claim. Stopping the poller on that would make a
        // live run look dead and invite a second Generate click (real
        // spend). Only status done|failed is terminal.
        //
        // NO client-side "timed out, stop polling" branch here on purpose —
        // that used to sit here and it was the bug (see POLL_SLOW_AFTER_MS's
        // comment above): a run that is genuinely still rendering must keep
        // being polled no matter how long it has already taken. The only
        // ceiling is the abandoned-tab safety valve below, which is minutes
        // of margin past the backend's own worst-case run lifetime, not a
        // verdict on this run.
        if (shouldKeepPolling(next.status)) {
          if (Date.now() - startedAt > POLL_HARD_STOP_MS) {
            setPollStopped(true);
            return;
          }
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        } else {
          // Terminal — nothing stale to carry forward.
          setPollStopped(false);
        }
      } catch (e) {
        if (cancelled) return;
        consecutiveFailures += 1;
        // Still refresh the ad list — the ads exist and are rendering even when
        // the progress lookup can't find the run.
        await fetchRef.current?.();
        if (cancelled) return;
        if (consecutiveFailures < MAX_RUN_POLL_FAILURES && Date.now() - startedAt <= POLL_HARD_STOP_MS) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        // Give up on PROGRESS only, and say so in those terms rather than
        // claiming the ads failed to load.
        const msg = e instanceof Error ? e.message : String(e);
        setRunErr(
          /not found/i.test(msg)
            ? 'Progress tracking unavailable for this batch — the ads below are still correct and will keep rendering.'
            : `Progress tracking stopped: ${msg}`
        );
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeBrandId, runBrandId, campaignRunId]);

  const headline = useMemo(() => {
    if (campaignRunId) return `Latest batch — ${total} creative${total === 1 ? '' : 's'}`;
    if (campaignId)    return `Campaign — ${total} creative${total === 1 ? '' : 's'}`;
    return `${total} creative${total === 1 ? '' : 's'}`;
  }, [total, campaignId, campaignRunId]);

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        eyebrow="Step 3 / 3"
        title="Ads"
        description="Every ad creative the renderer has produced for this brand. Filter by campaign or status; click for the full creative + copy + tracking URL."
      />

      {/* Campaign filter — primary slice of the page. Pulled out of
          the secondary toolbar and styled prominently so operators
          can re-scope the gallery at a glance. */}
      <Card variant="outline" borderColor={campaignId ? 'brand.accent' : 'brand.border'} borderWidth={campaignId ? '2px' : '1px'}>
        <CardBody py={3} px={4}>
          <HStack spacing={4} align="center" wrap="wrap">
            <Text
              fontSize="11px"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.1em"
              color="brand.muted"
              flexShrink={0}
            >
              Campaign
            </Text>
            <Select
              size="md"
              fontSize="sm"
              fontWeight="700"
              width={{ base: '100%', md: '380px' }}
              value={campaignId || ''}
              onChange={(e) => setCampaignFilter(e.target.value)}
              placeholder=""
            >
              <option value="">All campaigns</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
            {campaignId && (
              <Badge colorScheme="purple" fontSize="10px" px={2} py={1} borderRadius="md">
                Filtered{activeCampaignName ? ` · ${activeCampaignName}` : ''}
              </Badge>
            )}
            <Box flex={1} />
            {campaignId && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setCampaignFilter('')}
              >
                Clear
              </Button>
            )}
          </HStack>
        </CardBody>
      </Card>

      <HStack justify="space-between" align="center" wrap="wrap" spacing={3}>
        <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em" color="brand.ink">
          {headline}
        </Heading>
        <HStack spacing={3}>
          <Select
            size="sm"
            width="140px"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="rendering">Rendering</option>
            <option value="draft">Draft</option>
            <option value="live">Live</option>
            <option value="archived">Archived</option>
            <option value="failed">Failed</option>
          </Select>
          <Button as={RouterLink} to="/generate-ads" variant="brand" size="sm">
            Generate Ads
          </Button>
        </HStack>
      </HStack>

      {run && (
        <RunProgress
          run={run}
          pollStopped={pollStopped}
          onGenerateMore={async () => {
            try {
              const res = await apiJson<{ campaignRunId: string }>(`/api/ads/runs`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ campaignId: run.campaignId })
              });
              const nextParams = new URLSearchParams(params);
              nextParams.set('campaignRunId', res.campaignRunId);
              nextParams.set('campaignId',    run.campaignId);
              setParams(nextParams, { replace: false });
              toast({ title: 'Next batch started', status: 'success', duration: 2500 });
            } catch (e) {
              toast({
                title:       'Could not start next batch',
                description: e instanceof Error ? e.message : String(e),
                status:      'error',
                duration:    5000
              });
            }
          }}
        />
      )}

      {loading && (
        <Box textAlign="center" py={6}>
          <Spinner size="md" />
        </Box>
      )}

      {err && (
        <Card variant="outline">
          <CardBody>
            <Text color="red.500" fontSize="sm">Failed to load ads: {err}</Text>
          </CardBody>
        </Card>
      )}

      {/* Progress-tracking trouble is NOT an ad-loading failure — the ads below
          are real and still rendering. Kept visually secondary for that reason. */}
      {runErr && !err && (
        <Card variant="outline" borderColor="orange.200" bg="orange.50">
          <CardBody>
            <Text color="orange.800" fontSize="sm">{runErr}</Text>
          </CardBody>
        </Card>
      )}

      {/* campaignRunId present means we KNOW a batch was just kicked off (that
          is literally how the operator got to this URL). An EMPTY gallery is
          not one thing: it is still-working (ads will land) OR the run already
          finished with nothing (identity-digest collision, no usable media).
          MEASURED defect (run_1787119100250_eef4d871, first ~60s): before the
          campaignRunId guard, the "run the wizard" CTA rendered on every page
          load because it keyed only on `rows.length === 0`. MEASURED defect
          (run_1787578615444_d2cdcb3e): after that guard, a `done` run with
          total=0 still showed "they will appear here as soon as the first one
          lands" indefinitely — the run was already closed, the backend had
          written why on errors[]. LatestBatchEmptyState distinguishes the
          two. The instructional CTA still shows only when there is no run
          context at all. */}
      {!loading && !err && rows.length === 0 && (
        campaignRunId ? (
          <LatestBatchEmptyState run={run} />
        ) : (
          <Card variant="outline">
            <CardBody>
              <VStack align="stretch" spacing={3} py={8} textAlign="center">
                <Text fontSize="sm" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
                  No rendered ads yet
                </Text>
                <Text color="brand.ink" fontWeight="600" fontSize="lg">
                  Run the Generate Ads wizard from a campaign to produce your first batch.
                </Text>
                <HStack justify="center" pt={2}>
                  <Button as={RouterLink} to="/campaigns" variant="outline" size="sm">
                    Pick a campaign
                  </Button>
                  <Button as={RouterLink} to="/generate-ads" variant="brand" size="sm">
                    Generate Ads
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )
      )}

      {/* Selection toolbar — appears once the operator ticks any
          checkbox. Image and video creatives both push; videos go
          through Meta's async upload + processing pipeline (~30s–3min
          per video) so larger video batches will take a while. */}
      {selectedIds.size > 0 && (
        <Card variant="outline" borderColor="blue.300" bg="blue.50">
          <CardBody py={3}>
            <HStack justify="space-between" align="center">
              <HStack spacing={3}>
                <Badge colorScheme="blue" fontSize="11px">
                  {selectedIds.size} selected
                </Badge>
                <Text fontSize="sm" color="brand.ink">
                  Push these ads to Meta as PAUSED creatives. Videos take a bit longer (Meta processes them async).
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Button size="sm" variant="ghost" onClick={clearSelection}>Clear</Button>
                <Button
                  size="sm"
                  variant="brand"
                  onClick={openPushModal}
                  isDisabled={!activeBrandId}
                >
                  Push {selectedIds.size} to Meta
                </Button>
              </HStack>
            </HStack>
          </CardBody>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
          {rows.map((ad) => {
            // Prefer the live pipeline stage over coarse status — video ads
            // flip to draft before titling finishes, and image ads stamp
            // renderStage too. The old helper dropped both of those.
            const statusBadge = resolveAdStatusBadge(
              ad.status,
              ad.kind,
              ad.renderStage ?? null,
              ad.renderStageAt
            );
            return (
            <Card
              key={ad.id}
              variant="outline"
              cursor="pointer"
              _hover={{ borderColor: 'brand.accent', transform: 'translateY(-1px)' }}
              transition="all 120ms"
              onClick={() => { setSelected(ad); detailModal.onOpen(); }}
            >
              {/* Uniform 1:1 tile — non-square ads letterbox inside
                  the shared frame so every card in the grid matches
                  height. Detail modal below still shows the ad at
                  its native aspect ratio. */}
              <Box
                position="relative"
                bg="gray.900"
                borderTopRadius="md"
                overflow="hidden"
                style={{ aspectRatio: '1 / 1' }}
              >
                {displayUrl(ad) ? (
                  <AdChromePreview
                    aspectRatio={ad.aspectRatio}
                    width={ad.width}
                    height={ad.height}
                    platformFormat={ad.platformFormat}
                    ctaText={ad.copy.cta_text || ad.ctaText}
                    density="compact"
                  >
                    {ad.kind === 'video' ? (
                      <HoverPlayVideo
                        src={gridDisplayUrl(ad) || ''}
                        poster={ad.posterUrl}
                        alt={ad.copy.headline || ad.template}
                      />
                    ) : (
                      <Image
                        src={displayUrl(ad) || ''}
                        alt={ad.copy.headline || ad.template}
                        width="100%"
                        height="100%"
                        objectFit="contain"
                        loading="lazy"
                      />
                    )}
                  </AdChromePreview>
                ) : (
                  <Box
                    w="100%" h="100%"
                    display="flex" alignItems="center" justifyContent="center"
                    color="gray.400"
                    fontSize="11px"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                  >
                    {statusBadge.inProgress
                      ? statusBadge.label
                      : ad.status === 'queued' ? 'Queued'
                      : ad.status === 'rendering' ? 'Rendering…'
                      : ad.status === 'failed' ? 'Render failed'
                      : 'No render'}
                  </Box>
                )}
                {/* WHY it failed, not just that it did. A bare "Render failed"
                    tile is what sent someone to the database to discover that a
                    prompt had been rejected on content policy — which no retry
                    can fix. Truncated here; the full string (and the prediction
                    id) live on the Render Activity board. */}
                {ad.status === 'failed' && ad.renderErrorMessage && (
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    bg="blackAlpha.700"
                    color="red.200"
                    fontSize="10px"
                    px={2}
                    py={1}
                    noOfLines={2}
                    title={ad.renderErrorMessage}
                  >
                    {ad.renderErrorMessage.split(' [')[0].split(' (HTTP')[0]}
                  </Box>
                )}
                <Badge
                  position="absolute"
                  top={2}
                  right={2}
                  colorScheme={adStatusBadgeColorScheme(statusBadge.tone)}
                  fontSize="9px"
                  textTransform="none"
                  maxW="75%"
                  whiteSpace="normal"
                  textAlign="right"
                  title={statusBadge.detail || undefined}
                >
                  <Box as="span" textTransform="uppercase">{statusBadge.label}</Box>
                  {statusBadge.detail && (
                    <Box as="span" fontWeight="600">
                      {' · '}{statusBadge.detail}
                    </Box>
                  )}
                </Badge>
                {/* Selection checkbox — clickable independent of the
                    card body so toggling doesn't open the detail modal. */}
                <Box
                  position="absolute"
                  top={2}
                  left={2}
                  bg="blackAlpha.700"
                  borderRadius="md"
                  p={1}
                  cursor="pointer"
                  onClick={(e) => { e.stopPropagation(); toggleSelect(ad.id); }}
                >
                  <Box
                    w="18px" h="18px"
                    borderRadius="sm"
                    borderWidth="2px"
                    borderColor={selectedIds.has(ad.id) ? 'blue.400' : 'whiteAlpha.700'}
                    bg={selectedIds.has(ad.id) ? 'blue.400' : 'transparent'}
                    display="flex" alignItems="center" justifyContent="center"
                    color="white"
                    fontSize="11px"
                    fontWeight="800"
                  >
                    {selectedIds.has(ad.id) ? '✓' : ''}
                  </Box>
                </Box>
                {/* Phase B — Photoreal pill (top-left of tile). Now
                    fires whenever the photoreal landed (legacy toggle
                    deprecated; display always prefers photoreal). */}
                {isShowingPhotoreal(ad) && (
                  <Badge
                    position="absolute" top={2} left={2}
                    colorScheme="purple" fontSize="9px"
                    px={1.5} py={0.5}
                    pointerEvents="none"
                  >
                    PHOTOREAL
                  </Badge>
                )}
                {/* Meta sync pill — bottom-right of the tile. Synced
                    pill links to Ads Manager; failed pill surfaces the
                    error in a tooltip. */}
                {ad.metaSyncStatus === 'synced' && (
                  <Badge
                    as="a"
                    href={ad.metaAdId
                      ? `https://www.facebook.com/adsmanager/manage/ads?selected_ad_ids=${ad.metaAdId}`
                      : 'https://www.facebook.com/adsmanager'}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    position="absolute" bottom={2} right={2}
                    colorScheme="blue" fontSize="9px"
                    cursor="pointer"
                  >
                    Synced ↗
                  </Badge>
                )}
                {ad.metaSyncStatus === 'failed' && (
                  <Badge
                    title={ad.metaSyncError || 'Push failed'}
                    position="absolute" bottom={2} right={2}
                    colorScheme="red" fontSize="9px"
                  >
                    Push failed
                  </Badge>
                )}
                {/* Vision-QC pill — bottom-left of the tile. Only for
                    something an operator needs to act on or note, same
                    "surface on the exceptional case, not the common one"
                    precedent as the renderErrorMessage strip above:
                    `disabled` (the QC flag is off globally) is excluded —
                    that's an environment fact, not a per-ad anomaly, and
                    would otherwise light up every single card. A clean
                    attempt-1 pass is likewise the expected default and
                    stays quiet. Suppressed on a failed render that's
                    already showing its own full-width error strip below
                    this box — the two would visually stack. */}
                {ad.visionQc && !ad.visionQc.disabled
                  && (ad.visionQc.skipped || !ad.visionQc.passed || ad.visionQc.regenerated)
                  && !(ad.status === 'failed' && ad.renderErrorMessage) && (
                  <Badge
                    position="absolute" bottom={2} left={2}
                    colorScheme={
                      ad.visionQc.skipped || !ad.visionQc.passed ? 'red' : 'yellow'
                    }
                    fontSize="9px"
                    title={ad.visionQc.reason || ad.visionQc.summary || undefined}
                  >
                    {ad.visionQc.skipped
                      ? 'QC skipped'
                      : !ad.visionQc.passed
                        ? 'QC failed'
                        : 'QC passed on retry'}
                  </Badge>
                )}
              </Box>
              <CardBody py={3}>
                <VStack align="stretch" spacing={1}>
                  <HStack justify="space-between" spacing={2}>
                    <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                      {ad.template}
                    </Text>
                    <Text fontSize="9px" color="brand.muted">{ad.aspectRatio}</Text>
                  </HStack>
                  {/* Media type + intent profile — on their own compact line
                      rather than crammed into the template/aspectRatio row
                      above. Media type always shows; intent profile is
                      omitted entirely (no row, no stray "·") when the ad
                      carries no funnelStage — true for most static ads and
                      any video ad that predates the funnel-stage feature. */}
                  <HStack spacing={1} fontSize="9px" color="brand.muted">
                    <Text>{mediaTypeLabel(ad.kind)}</Text>
                    {funnelStageLabel(ad.funnelStage) && (
                      <>
                        <Text>·</Text>
                        <Text>{funnelStageLabel(ad.funnelStage)}</Text>
                      </>
                    )}
                  </HStack>
                  <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={2}>
                    {ad.copy.headline || '—'}
                  </Text>
                  {ad.copy.productName && (
                    <Text fontSize="11px" color="brand.muted" noOfLines={1}>
                      {ad.copy.productName} {ad.copy.productPrice ? `· ${ad.copy.productPrice}` : ''}
                    </Text>
                  )}
                  {/* Dev preview — three-column render-stage comparison
                      page (LLM HTML vs Puppeteer overlay PNG vs final
                      composite). Lets us spot variance the pipeline is
                      introducing. Token embedded in URL so window.open
                      (which can't set Authorization headers) still
                      authenticates via the server-side adapter middleware
                      that lifts ?_token= to the header before requireAuth.
                      Only renders when the ad has actually rendered. */}
                  {(ad.renderUrl || ad.photorealUrl) && (
                    <Link
                      fontSize="10px"
                      color="brand.muted"
                      href={`/api/ads/${ad.id}/preview-page?_token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                      isExternal
                      onClick={(e) => e.stopPropagation()}
                      _hover={{ color: 'rsViolet.500', textDecoration: 'underline' }}
                    >
                      Preview render stages →
                    </Link>
                  )}
                </VStack>
              </CardBody>
            </Card>
            );
          })}
        </SimpleGrid>
      )}

      <Modal
        isOpen={detailModal.isOpen}
        onClose={() => { setShowInspector(false); detailModal.onClose(); }}
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selected?.copy.headline || 'Ad detail'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selected && (
              <VStack align="stretch" spacing={4}>
                {displayUrl(selected) ? (
                  <VStack align="stretch" spacing={2}>
                    <AdChromeFrame
                      chrome={detailChrome}
                      ratio={selectedRatio}
                      identity={chromeIdentity}
                      density="full"
                    >
                      {selected.kind === 'video' ? (
                        <video
                          key={renderBust || selected.renderUrl || 'video'}
                          src={(() => {
                            const u = displayUrl(selected);
                            if (!u) return undefined;
                            return renderBust ? `${u}${u.includes('?') ? '&' : '?'}_cb=${renderBust}` : u;
                          })()}
                          poster={selected.posterUrl || undefined}
                          controls
                          playsInline
                          preload="metadata"
                          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
                        />
                      ) : (
                        <Image src={displayUrl(selected) || ''} alt={selected.copy.headline || selected.template} width="100%" height="100%" objectFit="contain" />
                      )}
                    </AdChromeFrame>
                    <HStack spacing={2} justify="center">
                      <Text fontSize="2xs" color="brand.muted" flexShrink={0}>Preview as</Text>
                      <ChromeSelect value={detailChrome} options={chromeOptions} onChange={setDetailChrome} />
                    </HStack>
                  </VStack>
                ) : (
                  <Box
                    bg="gray.900" borderRadius="md" overflow="hidden"
                    style={{ aspectRatio: `${selected.width} / ${selected.height}` }}
                    display="flex" alignItems="center" justifyContent="center"
                    color="gray.400" fontSize="12px"
                  >
                    {selected.status === 'queued' ? 'Queued for render' : selected.status === 'rendering' ? 'Rendering…' : selected.status === 'failed' ? 'Render failed' : 'No render'}
                  </Box>
                )}
                <SimpleGrid columns={2} spacing={3}>
                  <DetailRow label="Template" value={selected.template} />
                  <DetailRow label="Aspect ratio" value={selected.aspectRatio} />
                  <DetailRow label="Media type" value={mediaTypeLabel(selected.kind)} />
                  {/* Intent profile — omitted entirely (not a "—" row) when
                      the ad has no funnelStage, per the field's own "absent
                      means nothing shown" contract. */}
                  {funnelStageLabel(selected.funnelStage) && (
                    <DetailRow label="Intent profile" value={funnelStageLabel(selected.funnelStage) as string} />
                  )}
                  <DetailRow label="Status" value={selected.status} />
                  <DetailRow label="Match tier" value={selected.matchTier} />
                  <DetailRow label="Variant" value={selected.variantKind} />
                  <DetailRow label="CTA URL" value={selected.ctaUrl} />
                  <DetailRow label="Generated" value={new Date(selected.generatedAt).toLocaleString()} />
                </SimpleGrid>
                {/* Retailer product-page link — null-safe: absent whenever
                    the ad has no productId (bulk soft-delete has nulled this
                    on some ads), the product was unlinked, or the product
                    has no productUrl on file. Server-side join is brand-
                    scoped (loadProductUrlMap) so this can never surface
                    another brand's product URL — see PR #245. href is
                    scheme-gated (http/https only): catalog productUrl is
                    tenant-writable and React will not strip javascript:.
                    isExternal sets target="_blank" rel="noopener noreferrer". */}
                {selected.productUrl && (
                  <Box>
                    <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                      Retailer product page
                    </Text>
                    {selectedProductHref ? (
                      <Link
                        href={selectedProductHref}
                        isExternal
                        fontSize="sm"
                        fontWeight="600"
                        color="rsViolet.600"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        View on retailer's site ↗
                      </Link>
                    ) : (
                      <Text fontSize="sm" fontWeight="600" color="brand.muted">
                        View on retailer's site ↗
                      </Text>
                    )}
                  </Box>
                )}
                {/* Vision-QC summary — previously the ONLY place to see this
                    at all was the "Generation details" inspector below.
                    `disabled` (QC flag off globally) is suppressed here too,
                    same reasoning as the gallery pill: an environment fact,
                    not something this ad did. Category-level findings still
                    live only in the inspector (this modal is fed by the
                    gallery's list fetch, not the detail/full projection) —
                    "Generation details" is the way to see those. */}
                {selected.visionQc && !selected.visionQc.disabled && (
                  <Box
                    borderWidth="1px"
                    borderColor={
                      selected.visionQc.skipped || !selected.visionQc.passed ? 'red.200' : 'gray.200'
                    }
                    bg={
                      selected.visionQc.skipped || !selected.visionQc.passed ? 'red.50' : 'gray.50'
                    }
                    borderRadius="md"
                    px={3} py={2}
                  >
                    <HStack justify="space-between" align="start">
                      <Box>
                        <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                          Vision QC
                        </Text>
                        <Text fontSize="sm" color="brand.ink">
                          {selected.visionQc.skipped
                            ? `Shipped without QC${selected.visionQc.reason ? ` — ${selected.visionQc.reason}` : ''}`
                            : selected.visionQc.passed
                              ? (selected.visionQc.regenerated ? 'Passed after one regeneration' : 'Passed')
                              : `Failed${selected.visionQc.summary ? ` — ${selected.visionQc.summary}` : ''}`}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={selected.visionQc.skipped || !selected.visionQc.passed ? 'red' : 'green'}
                        fontSize="9px"
                      >
                        {selected.visionQc.skipped ? 'SKIPPED' : selected.visionQc.passed ? 'PASS' : 'FAIL'}
                      </Badge>
                    </HStack>
                  </Box>
                )}
                <HStack spacing={2}>
                  <Box>
                    <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                      Ad ID
                    </Text>
                    <Text fontSize="sm" color="brand.ink" fontWeight="600" fontFamily="mono">{selected.id}</Text>
                  </Box>
                  <Button
                    size="xs" variant="ghost"
                    onClick={() => void navigator.clipboard.writeText(selected.id).then(
                      () => toast({ title: 'Ad ID copied', status: 'success', duration: 2000 }),
                      () => toast({ title: 'Copy failed', description: selected.id, status: 'error', duration: 4000 })
                    )}
                  >
                    Copy
                  </Button>
                </HStack>
                {/* Editable title copy — PATCH only changed keys; video ads can
                    re-render titles standalone (e.g. after a brand placement-
                    mode change) without requiring a prior copy edit. */}
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                    Title copy
                  </Text>
                  {(() => {
                    const copyDirty =
                      editHeadline.trim() !== (selected.copy.headline || '').trim() ||
                      editCta.trim() !== (selected.copy.cta_text || '').trim();
                    const isExported = selected.metaSyncStatus === 'synced';
                    const exportedTitle = isExported ? 'Exported ads are read-only' : undefined;
                    return (
                      <>
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="brand.muted" w="72px" flexShrink={0}>Headline</Text>
                          <Input
                            size="sm"
                            value={editHeadline}
                            maxLength={300}
                            onChange={e => setEditHeadline(e.target.value)}
                            isDisabled={copySaving || titleRerendering || isExported}
                            title={exportedTitle}
                          />
                        </HStack>
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="brand.muted" w="72px" flexShrink={0}>CTA</Text>
                          <Input
                            size="sm"
                            value={editCta}
                            maxLength={300}
                            onChange={e => setEditCta(e.target.value)}
                            isDisabled={copySaving || titleRerendering || isExported}
                            title={exportedTitle}
                          />
                        </HStack>
                        {selected.kind !== 'video' && (
                          <Text fontSize="11px" color="brand.muted">
                            Saved copy applies to future renders — image creatives are not re-rendered here.
                          </Text>
                        )}
                        <HStack spacing={2} justify="flex-end" flexWrap="wrap">
                          {titleRerendering && (
                            <HStack spacing={1} mr="auto">
                              <Spinner size="xs" />
                              <Text fontSize="xs" color="brand.muted">Re-rendering title… this can take tens of seconds.</Text>
                            </HStack>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void saveSelectedCopy()}
                            isLoading={copySaving}
                            isDisabled={!copyDirty || titleRerendering || isExported}
                            title={exportedTitle}
                          >
                            Save copy
                          </Button>
                          {selected.kind === 'video' && (
                            <Button
                              size="sm"
                              variant="brand"
                              onClick={() => void rerenderSelectedTitle()}
                              isLoading={titleRerendering}
                              loadingText="Re-rendering…"
                              // Re-title must be runnable standalone (e.g. after a
                              // brand placement-mode change) — only gate on unsaved
                              // draft, not on having just saved a copy edit.
                              isDisabled={copyDirty || copySaving || titleRerendering || isExported}
                              title={
                                isExported
                                  ? 'Exported ads are read-only'
                                  : copyDirty
                                    ? 'Save pending edits before re-rendering'
                                    : undefined
                              }
                            >
                              Re-render title
                            </Button>
                          )}
                        </HStack>
                      </>
                    );
                  })()}
                </VStack>
                {selected.copy.quote && (
                  <DetailRow label="Quote" value={selected.copy.quote} />
                )}
                <HStack justify="space-between" spacing={2}>
                  <Button
                    onClick={() => deleteAd(selected)}
                    variant="outline"
                    colorScheme="red"
                    size="sm"
                    isDisabled={actioning}
                  >
                    Delete
                  </Button>
                  <HStack spacing={2}>
                    {selected.id && activeBrandId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowInspector(true)}
                      >
                        Generation details
                      </Button>
                    )}
                    {selected.renderUrl && (
                      <Button as="a" href={selected.renderUrl} target="_blank" variant="outline" size="sm">
                        Open in Cloudinary
                      </Button>
                    )}
                    {selected.status === 'draft' && (
                      <>
                        <Button
                          onClick={() => setAdStatus(selected, 'archived')}
                          variant="outline"
                          size="sm"
                          isDisabled={actioning}
                        >
                          Archive
                        </Button>
                        <Button
                          onClick={() => setAdStatus(selected, 'live')}
                          variant="brand"
                          size="sm"
                          isDisabled={actioning}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                    {selected.status === 'live' && (
                      <Button
                        onClick={() => setAdStatus(selected, 'archived')}
                        variant="outline"
                        size="sm"
                        isDisabled={actioning}
                      >
                        Archive
                      </Button>
                    )}
                    {selected.status === 'archived' && (
                      <Button
                        onClick={() => setAdStatus(selected, 'draft')}
                        variant="brand"
                        size="sm"
                        isDisabled={actioning}
                      >
                        Restore to draft
                      </Button>
                    )}
                  </HStack>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Generation inspector — mounted as a SIBLING of the detail modal, not
          inside it. Nesting one Chakra Modal inside another fights over focus
          trap and scroll lock, and the inspector renders its own overlay.

          brandId comes from the AD ROW, not the global switcher. The inspector
          endpoint is brand-scoped, so keying it on activeBrandId reproduces
          exactly the bug fixed earlier in this file: an ad belonging to another
          brand 404s when the switcher happens to be elsewhere. The row already
          carries the authoritative brandId. */}
      {showInspector && selected?.id && selected?.brandId && (
        <ModalErrorBoundary what="generation details" onDismiss={() => setShowInspector(false)}>
          <GenerationInspectorModal
            adId={selected.id}
            brandId={selected.brandId}
            onClose={() => setShowInspector(false)}
          />
        </ModalErrorBoundary>
      )}

      {/* Push-to-Meta modal — single AdSet dropdown grouped by Meta
          Campaign. Submitting fans out the selected adIds to the
          backend's batch endpoint; the toast summarizes the per-ad
          result and the gallery refreshes to show the new pills. */}
      <Modal isOpen={pushModal.isOpen} onClose={pushModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Push {selectedIds.size} ad{selectedIds.size === 1 ? '' : 's'} to Meta</ModalHeader>
          <ModalCloseButton isDisabled={pushing} />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="brand.muted">
                Pick the AdSet that the new ads will belong to. They'll be created as <strong>PAUSED</strong> — activate them in Meta Ads Manager when you're ready.
              </Text>
              {adsetsLoading ? (
                <HStack py={4} justify="center"><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading AdSets…</Text></HStack>
              ) : metaAdsets.length === 0 ? (
                <Box bg="orange.50" borderColor="orange.300" borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontSize="sm" color="orange.700" fontWeight="700">No Meta AdSets found.</Text>
                  <Text fontSize="xs" color="brand.muted" mt={1}>
                    Connect Meta Ads on the Brand page and run a campaign sync, then come back here.
                  </Text>
                </Box>
              ) : (
                <Box>
                  <Text fontSize="xs" fontWeight="800" color="brand.muted" textTransform="uppercase" letterSpacing="0.06em" mb={1}>
                    Target AdSet
                  </Text>
                  <Select
                    value={chosenAdsetId}
                    onChange={(e) => setChosenAdsetId(e.target.value)}
                    placeholder="Pick an AdSet…"
                  >
                    {adsetsByCampaign.map(group => (
                      <optgroup key={group.campaignName} label={group.campaignName}>
                        {group.adsets.map(a => (
                          <option key={a.adsetId} value={a.adsetId}>
                            {a.adsetName}{a.adsetStatus ? ` · ${a.adsetStatus}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </Box>
              )}
              <HStack justify="flex-end" spacing={2}>
                <Button variant="outline" size="sm" onClick={pushModal.onClose} isDisabled={pushing}>Cancel</Button>
                <Button
                  variant="brand"
                  size="sm"
                  onClick={submitPush}
                  isDisabled={!chosenAdsetId || pushing}
                  isLoading={pushing}
                  loadingText="Pushing…"
                >
                  Push as PAUSED
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
        {label}
      </Text>
      <Text fontSize="sm" color="brand.ink" fontWeight="600">
        {value || '—'}
      </Text>
    </Box>
  );
}

// Empty gallery under a campaignRunId. Distinguishes still-working
// (spinner + waiting copy) from a terminal run that produced nothing
// (the backend's own sentence). Exported for __harness__/runNotice.tsx.
export function LatestBatchEmptyState({ run }: { run: RunStatus | null }) {
  const outcome = classifyLatestBatch({ run, rowCount: 0 });
  // Terminal run that DID produce ads (total>0) but this view has 0 rows
  // (filter / fetch lag). RunProgress already has the real count; claiming
  // "produced nothing" here would invite a second Generate.
  if (outcome === 'finished-with-results') return null;
  const waiting = outcome === 'waiting';
  const copy = latestBatchEmptyCopy(run);
  return (
    <Card
      variant="outline"
      data-testid="latest-batch-empty"
      data-outcome={outcome}
      borderColor={waiting ? undefined : 'orange.200'}
      bg={waiting ? undefined : 'orange.50'}
    >
      <CardBody>
        <VStack align="stretch" spacing={2} py={8} textAlign="center">
          {waiting ? (
            <Spinner size="sm" />
          ) : (
            <Text
              fontSize="sm"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.06em"
              color="brand.muted"
            >
              Batch finished — 0 creatives
            </Text>
          )}
          <Text
            color={waiting ? 'brand.muted' : 'brand.ink'}
            fontSize="sm"
            fontWeight={waiting ? undefined : '600'}
          >
            {copy}
          </Text>
        </VStack>
      </CardBody>
    </Card>
  );
}

// Exported for src/pages/Ads/__harness__/visionQc.tsx — same reasoning as
// resolveAdStatusBadge/stageBucketLabel above: this repo has no test runner
// for the SPA, so a dev-only fixture harness exercising the REAL component
// (not a re-implementation of its JSX) is how a change here gets checked
// without a backend, an authenticated session, or a live run.
export function RunProgress({
  run, pollStopped, onGenerateMore
}: {
  run: RunStatus;
  pollStopped: boolean;
  onGenerateMore: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const finished = run.succeeded + run.skipped + run.failed;
  const remaining = Math.max(0, run.total - finished);
  const pct = run.total > 0 ? Math.round((finished / run.total) * 100) : 0;

  const handleGenerateMore = async () => {
    setBusy(true);
    try { await onGenerateMore(); }
    finally { setBusy(false); }
  };

  // "Generate more" affordance — appears whenever the run has settled
  // (done or failed) and there's queued inventory left for this campaign.
  // Drains the next batch via POST /api/ads/runs.
  //
  // Deliberately NOT offered while merely `pollStopped` (auto-refresh gave
  // up watching) — that says nothing about whether the run itself is still
  // rendering, and offering a second concurrent batch on top of one that
  // may well still be actively billing is exactly the kind of thing the
  // backend's own concurrency/fingerprint gate exists to catch, not
  // something this card should invite.
  //
  // Included for 'failed' because a deploy SIGTERM or the backend's
  // preparing-run reaper can leave a run in exactly this state with a
  // fully queued backlog — POST /api/ads/runs works fine against such a
  // campaign, so there's no reason to force the operator over to the
  // Campaigns page to drain it.
  const generateMoreBtn = (run.queuedRemaining > 0 && (run.status === 'done' || run.status === 'failed')) ? (
    <Button
      variant="brand"
      size="sm"
      onClick={handleGenerateMore}
      isLoading={busy}
      loadingText="Starting…"
    >
      Generate more ({run.queuedRemaining} queued)
    </Button>
  ) : null;

  // Backend notice — present once claim has run, same { code, message }
  // shape the /generate 202 uses. 'minted-ads-unclaimed' means THIS run's
  // mint outgrew what it claimed (a stranded backlog) and stays relevant
  // for the run's lifetime. The overlap notice
  // ('concurrent-run-shares-products') is stamped at mint and never
  // refreshed — on a settled run its present-tense "another run is in
  // progress" is stale, so it only renders while this run is still
  // preparing/running (the 202 toast already delivered it once).
  const runSettled = run.status === 'done' || run.status === 'failed';
  const noticeIsStaleOverlap =
    runSettled && run.notice?.code === 'concurrent-run-shares-products';
  const noticeAlert = (run.notice?.message && !noticeIsStaleOverlap) ? (
    <Alert
      status={run.notice.code === 'minted-ads-unclaimed' ? 'warning' : 'info'}
      borderRadius="md"
      fontSize="sm"
    >
      <AlertIcon />
      <Box flex="1">
        <Text fontSize="sm">{run.notice.message}</Text>
      </Box>
    </Alert>
  ) : null;

  // Honest leftover count when this run's mint outgrew its claim. Uses the
  // backend's set difference (unclaimedAtStart = minted ids THIS run did not
  // claim) — never mintedTotal - total, which double-counts old leftovers the
  // claim absorbed (34 minted / 20 claimed of which 5 old = 19 left, not 14).
  // Past tense on purpose: a later "Generate more" may have drained them.
  // Suppressed when the minted-ads-unclaimed Alert is shown — that message
  // already carries these exact numbers, and two copies can only disagree.
  const mintedNote = (
    typeof run.mintedTotal === 'number'
    && typeof run.unclaimedAtStart === 'number'
    && run.unclaimedAtStart > 0
    && run.notice?.code !== 'minted-ads-unclaimed'
  )
    ? ` (${run.mintedTotal} minted — ${run.unclaimedAtStart} left queued at claim time)`
    : '';

  // Grouped, deduped failure reasons — the SAME grouping
  // (services/runFeedService.js summariseFailures) Slack's own run-finished
  // summary uses, reused verbatim via GET /api/ads/runs/:id's
  // `failureSummary` so this card and the #status Slack channel can never
  // describe a failure differently. Shown whenever anything in this run has
  // failed, including while the run is still 'running' — a partial failure
  // (one shared video master timing out, say) must be explained the moment
  // it happens, not only once the whole batch finishes. MEASURED gap this
  // closes (run_1787119100250_eef4d871): Slack named the exact failed
  // master, its provider id, and that every derived surface was skipped as
  // a direct consequence ("master status=failed has no veoVideoUrl — not
  // submitting Omni"); the app said nothing beyond a raw failed-count.
  const operatorSentence = runTerminalExplanation(run);
  const failureDetail = run.failed > 0 ? (
    <VStack align="stretch" spacing={1}>
      {run.failureSummary && run.failureSummary.length > 0 && (
        <Text fontSize="12px" color="red.700" fontWeight="700">
          {run.failureSummary.map(f => `${f.count}✗ ${f.reason}`).join('   ·   ')}
        </Text>
      )}
      {run.errors.slice(0, 5).map((e, i) => (
        <Text key={i} fontSize="11px" color="brand.muted" fontFamily="mono" noOfLines={1}>
          #{e.index} {e.template}/{e.aspectRatio} ({e.stage}): {e.message}
        </Text>
      ))}
      {run.errors.length > 5 && (
        <Text fontSize="11px" color="brand.muted">
          + {run.errors.length - 5} more — check server logs for the full list.
        </Text>
      )}
    </VStack>
  ) : (run.status === 'failed' && operatorSentence) ? (
    // Expand-throw path stamps status=failed with failed=0 and a real
    // errors[].message. Do NOT use this fallback while 'running' —
    // noticeAlert already shows mint-time notices, and duplicating them
    // into the live progress card is noise.
    <Text fontSize="sm" color="brand.ink">{operatorSentence}</Text>
  ) : null;

  // What is this batch's in-flight work doing RIGHT NOW — the same "now: …"
  // aggregate Slack's live feed already shows for this run
  // (services/runFeedService.js buildParentText /
  // services/adStage.js summarizeInFlightStages), labelled through the exact
  // same table an ad card's own badge uses (stageBucketLabel above) so the
  // wording here never disagrees with a card underneath it. Re-aggregated
  // by LABEL (not raw stage string) because several raw stages collapse to
  // one label — e.g. "titling 9:16" and "titling recovered master" both
  // read "Titling", and should count together.
  let stageLine: string | null = null;
  if (run.stages && run.stages.length > 0) {
    const byLabel = new Map<string, number>();
    for (const { stage, count } of run.stages) {
      const { label } = stageBucketLabel(stage);
      byLabel.set(label, (byLabel.get(label) || 0) + count);
    }
    stageLine = [...byLabel.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, n]) => (n > 1 ? `${label} ×${n}` : label))
      .join('   ·   ');
  }

  // TRUTHFUL liveness hint — reuses services/campaignRunHeartbeat.js's own
  // field (PR #220), the same signal the backend's reaper trusts, rather
  // than a client-invented timer. Only meaningful for 'running': a
  // 'preparing' run never heartbeats by design (expandWizardJob makes zero
  // writes to the CampaignRun row until the flip), so computing silence
  // there would flag every normal Director/Judge round as "stalled".
  // Additive only — it NEVER replaces the live progress below. That
  // replacement is exactly what the old `timedOut` branch used to do
  // (see POLL_SLOW_AFTER_MS's comment above) and is the measured defect:
  // the progress bar, percentage, and succeeded/failed breakdown all
  // vanished behind a permanent "Timed out" card while a PMax master was
  // still genuinely rendering.
  // Run-level vision-QC rollup — GET /api/ads/runs/:runId `visionQcRollup`,
  // itself backed by the same Ad.visionQc verdicts the generation-inspector
  // and Slack's per-ad QC alerts already read (services/adVisionQcService.js
  // summarizeVisionQc / alertQcSkipped). Unlike stageLine/failureSummary
  // above, there is no Slack run-level equivalent to stay consistent with —
  // Slack only ever alerts per-ad — so this is the first place any of these
  // counts is aggregated at all. Absent (not 0) on an older backend; all
  // three render as silence either way (see the field's own doc comment on
  // the RunStatus type — absent must never be read as "proven clean").
  //
  // THREE STATES, not a binary "problem / no problem": not-inspected
  // (shippedWithoutQc) and inspected-and-flagged (qcFailed) are visually
  // distinct here on purpose — "QC never ran" and "QC ran and caught
  // something" are different facts an operator needs to act on
  // differently. Kept quiet on a fully clean batch (all three are 0): same
  // "only surface when it's actionable" precedent as renderErrorMessage /
  // failureDetail elsewhere on this page.
  const shippedWithoutQc = run.visionQcRollup?.shippedWithoutQc ?? 0;
  const qcFailed = run.visionQcRollup?.qcFailed ?? 0;
  const qcdOnRetry = run.visionQcRollup?.qcdOnRetry ?? 0;
  const visionQcNote = (shippedWithoutQc > 0 || qcFailed > 0 || qcdOnRetry > 0) ? (
    <Text
      fontSize="11px"
      color={(shippedWithoutQc > 0 || qcFailed > 0) ? 'red.700' : 'brand.muted'}
      fontWeight={(shippedWithoutQc > 0 || qcFailed > 0) ? '700' : undefined}
    >
      {shippedWithoutQc > 0 && `⚠ ${shippedWithoutQc} not inspected (vision QC didn't run)`}
      {shippedWithoutQc > 0 && (qcFailed > 0 || qcdOnRetry > 0) && '   ·   '}
      {qcFailed > 0 && `⚠ ${qcFailed} flagged by vision QC`}
      {qcFailed > 0 && qcdOnRetry > 0 && '   ·   '}
      {qcdOnRetry > 0 && `${qcdOnRetry} QC'd on retry`}
    </Text>
  ) : null;

  let silenceNote: string | null = null;
  if (run.status === 'running') {
    const last = run.lastHeartbeatAt || run.updatedAt;
    const lastMs = last ? Date.parse(last) : NaN;
    if (Number.isFinite(lastMs)) {
      const silentMs = Date.now() - lastMs;
      if (silentMs > POLL_SLOW_AFTER_MS) {
        silenceNote = `No update from the renderer in ${formatElapsedMs(silentMs)} — ` +
          'this can happen briefly between items on a large batch; still watching.';
      }
    }
  }

  if (run.status === 'done') {
    if (run.failed === 0) {
      // A done run with total===0 is not a quiet success — it is a
      // finished-empty batch (identity-digest collision, no usable
      // media, etc.). The gallery empty-state (LatestBatchEmptyState)
      // carries the operator sentence so we do not duplicate it here.
      // This card only stays up when there is something ELSE to act on
      // (Generate more / minted leftover notice / vision QC). Never
      // paint it green: "Run complete — 0 rendered" read as success
      // next to a spinner that said "still waiting".
      if (run.total === 0) {
        if (!generateMoreBtn && !noticeAlert && !visionQcNote) return null;
        return (
          <VStack align="stretch" spacing={2} data-testid="run-progress" data-run-status="done-empty">
            <Card variant="outline" borderColor="orange.200" bg="orange.50">
              <CardBody>
                <VStack align="stretch" spacing={1}>
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight="700" color="brand.ink">
                      Batch finished — 0 creatives{mintedNote}
                    </Text>
                    {generateMoreBtn}
                  </HStack>
                  {visionQcNote}
                </VStack>
              </CardBody>
            </Card>
            {noticeAlert}
          </VStack>
        );
      }
      // Full success — quiet card with only the "Generate more"
      // affordance when there's queued inventory remaining, or a still-
      // relevant notice (stale overlap notices are already filtered out of
      // noticeAlert above, so its presence here means it matters). A clean
      // succeeded/skipped/failed count is NOT the whole story, though — a
      // batch can be "done, zero renders failed" and still have shipped
      // ads nobody inspected, so visionQcNote keeps this card from going
      // quiet in exactly that case.
      if (!generateMoreBtn && !noticeAlert && !visionQcNote) return null;
      return (
        <VStack align="stretch" spacing={2}>
          <Card variant="outline" borderColor="green.300" bg="green.50">
            <CardBody>
              <VStack align="stretch" spacing={1}>
                <HStack justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="700" color="green.700">
                    {mintedNote
                      ? `${run.succeeded} of ${run.total} claimed${mintedNote}.`
                      : `Run complete — ${run.succeeded} rendered.`}
                  </Text>
                  {generateMoreBtn}
                </HStack>
                {visionQcNote}
              </VStack>
            </CardBody>
          </Card>
          {noticeAlert}
        </VStack>
      );
    }
    return (
      <VStack align="stretch" spacing={2}>
        <Card variant="outline" borderColor="red.300" bg="red.50">
          <CardBody>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontWeight="700" color="brand.ink">
                  {run.succeeded} succeeded · {run.skipped} skipped · {run.failed} failed
                </Text>
                <Badge colorScheme="red">{run.failed} error{run.failed === 1 ? '' : 's'}</Badge>
              </HStack>
              {mintedNote && (
                <Text fontSize="11px" color="brand.muted">
                  {run.total} claimed this run{mintedNote}.
                </Text>
              )}
              {visionQcNote}
              {failureDetail}
              {generateMoreBtn && <HStack justify="flex-end" pt={1}>{generateMoreBtn}</HStack>}
            </VStack>
          </CardBody>
        </Card>
        {noticeAlert}
      </VStack>
    );
  }

  if (run.status === 'failed') {
    return (
      <VStack align="stretch" spacing={2}>
        <Card variant="outline" borderColor="red.400" bg="red.50">
          <CardBody>
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="700" color="red.700">Run crashed before completing</Text>
              {visionQcNote}
              {run.errors.length > 0 ? (
                // The reaper and the queued-drain crash path now both push a
                // real errors[] entry (previously some left it empty — the
                // exact "operator-blind" gap this whole change is about), so
                // prefer that over a blanket "check the server logs".
                failureDetail || (
                  <Text fontSize="sm" color="brand.muted">Check the server logs for the underlying error.</Text>
                )
              ) : (
                <Text fontSize="sm" color="brand.muted">Check the server logs for the underlying error.</Text>
              )}
              {generateMoreBtn && (
                <>
                  <Text fontSize="sm" color="brand.muted">
                    {/* queuedRemaining is CAMPAIGN-wide (the backend counts every queued
                        ad on the campaign), so say "on this campaign", not "from this
                        run" — after a reaped prepare, most of these can be older mints. */}
                    {run.queuedRemaining} queued ad{run.queuedRemaining === 1 ? '' : 's'} on this
                    campaign can still be rendered.
                  </Text>
                  <HStack justify="flex-end">{generateMoreBtn}</HStack>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
        {noticeAlert}
      </VStack>
    );
  }

  // Preparing — expandWizardJob is running in the background; total isn't
  // known yet, so show an indeterminate state instead of "0 of 0".
  //
  // The copy deliberately does NOT name a pipeline stage. It used to say
  // "building the concept brief", which asserted a Director + Judge round
  // that does not happen on the default path: for a product video run with
  // the director toggle off, routing computes conceptVideo=false and only
  // expandDeterministicVideo runs — no concepts, no judging. Naming a stage
  // the run may not be executing is worse than staying general, and this
  // component has no way to know which expansion the backend chose (the
  // /runs/:id poll returns status and counts, not mode).
  if (run.status === 'preparing') {
    return (
      <VStack align="stretch" spacing={2}>
        <Card variant="outline" borderColor="purple.300" bg="purple.50">
          <CardBody>
            <HStack spacing={2}>
              <Spinner size="sm" color="purple.500" />
              <Text fontWeight="700" color="brand.ink">
                Preparing creatives…
              </Text>
            </HStack>
          </CardBody>
        </Card>
        {noticeAlert}
      </VStack>
    );
  }

  // Running — show live progress. Never swapped out for a terminal-looking
  // card just because it is taking a while (see POLL_SLOW_AFTER_MS above);
  // `silenceNote` and `pollStopped` are additive cautions only.
  return (
    <VStack align="stretch" spacing={2}>
      <Card
        variant="outline"
        borderColor={silenceNote ? 'orange.300' : 'purple.300'}
        bg={silenceNote ? 'orange.50' : 'purple.50'}
      >
        <CardBody>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Spinner size="sm" color={silenceNote ? 'orange.500' : 'purple.500'} />
                <Text fontWeight="700" color="brand.ink">
                  Rendering — {finished} of {run.total} complete
                </Text>
              </HStack>
              <Badge colorScheme={silenceNote ? 'orange' : 'purple'}>{pct}%</Badge>
            </HStack>
            <Progress value={pct} size="sm" colorScheme={silenceNote ? 'orange' : 'purple'} borderRadius="md" />
            {/* Explicit, never-ambiguous counts. A bare "N of M complete" can
                straddle two very different meanings — measured on
                run_1787119100250_eef4d871, where "18 of 39" read as "18
                failed" when it was actually the SUCCESS count (18 others had
                separately failed) — so success / failure / still-rendering
                are always broken out as distinct, explicitly labelled
                numbers, not folded into one ambiguous fraction. */}
            <Text fontSize="11px" color="brand.muted">
              {run.succeeded} succeeded · {run.skipped} skipped · {run.failed} failed · {remaining} still rendering{mintedNote}
            </Text>
            {stageLine && (
              <Text fontSize="11px" color="brand.muted">
                now: {stageLine}
              </Text>
            )}
            {silenceNote && (
              <Text fontSize="11px" color="orange.700">{silenceNote}</Text>
            )}
            {visionQcNote}
            {failureDetail}
          </VStack>
        </CardBody>
      </Card>
      {pollStopped && (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box flex="1">
            <Text fontSize="sm">
              This page stopped auto-refreshing after watching for a while. The batch
              itself is not affected — reload the page to check its current status.
            </Text>
          </Box>
        </Alert>
      )}
      {noticeAlert}
    </VStack>
  );
}
